import { a as __rest } from '../../tslib.es6.esm.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { forwardRef, useId, useState, useEffect, useMemo } from 'react';
import { CheckboxCheckmarkIcon } from '@iris/react-icons/CheckboxCheckmarkIcon';
import { CheckboxDashIcon } from '@iris/react-icons/CheckboxDashIcon';
import { Root, Indicator } from '@radix-ui/react-checkbox';
import baseCheckboxStyles from '../BaseCheckbox/BaseCheckbox.styles.esm.js';
import styles from './Checkbox.styles.esm.js';
import { CHECKBOX_STATES, CHECKBOX_KEYS } from './constants.esm.js';
import { getDataState } from './utils/geDataState.esm.js';
import { getAriaChecked } from './utils/getAriaChecked.esm.js';
import { cn } from '../../utils/cn.esm.js';

const Checkbox = forwardRef((props, ref) => {
    const generatedId = useId();
    const { id = generatedId, inverse, error, disabled, indeterminate, "aria-label": ariaLabel, label, onClick, checked, value, className } = props, otherProps = __rest(props, ["id", "inverse", "error", "disabled", "indeterminate", "aria-label", "label", "onClick", "checked", "value", "className"]);
    const classes = styles({
        hasLabel: Boolean(label),
        inverse,
        error,
    });
    const [isChecked, setIsChecked] = useState(checked !== null && checked !== void 0 ? checked : false);
    useEffect(() => {
        setIsChecked(checked !== null && checked !== void 0 ? checked : false);
    }, [checked]);
    const baseCheckboxClasses = useMemo(() => baseCheckboxStyles({ inverse, disabled, error }), [inverse, disabled, error]);
    const onCheckedChange = (newChecked) => {
        setIsChecked(newChecked);
        if (otherProps.onCheckedChange) {
            otherProps.onCheckedChange(Boolean(newChecked));
        }
    };
    const onKeyDown = (e) => {
        if (e.key === CHECKBOX_KEYS.SPACE && !disabled) {
            e.preventDefault();
            const newChecked = !isChecked;
            setIsChecked(newChecked);
            if (otherProps.onCheckedChange) {
                otherProps.onCheckedChange(newChecked);
            }
        }
    };
    return (jsx(Root, Object.assign({ disabled: disabled, "aria-label": ariaLabel ? ariaLabel : label, onKeyDown: onKeyDown, className: cn(classes.container(), className), "data-state": getDataState(disabled, isChecked), "aria-checked": getAriaChecked(isChecked, indeterminate), checked: isChecked, onCheckedChange: onCheckedChange, id: id, onClick: onClick, ref: ref }, otherProps, { children: jsxs("div", { className: classes.gapContainer(), children: [jsx("div", { className: classes.checkboxWrapper(), children: jsx("div", { "data-state": isChecked ? CHECKBOX_STATES.CHECKED : CHECKBOX_STATES.UNCHECKED, className: cn(baseCheckboxClasses.base(), classes.checkbox()), children: jsx(Indicator, { className: cn(classes.indicator(), indeterminate ? "iris:top-[45%]" : "iris:top-[10%]"), children: indeterminate ? (jsx(CheckboxDashIcon, {})) : (jsx(CheckboxCheckmarkIcon, {})) }) }) }), label && (jsx("label", { className: classes.label(), htmlFor: id, "aria-hidden": true, children: label }))] }) })));
});
Checkbox.displayName = "Checkbox";

export { Checkbox };
